/**
 * Data Utilities
 * Functions for loading, validating, and managing tea ceremony data
 */

import { validatePersonData, validateItemsData } from './dataValidation.js';

/**
 * Loads people data from people.json
 * @returns {Promise<{data: Array|null, error: string|null, isLoading: boolean}>}
 */
export async function loadPeopleData() {
  try {
    const response = await fetch('/people.json');

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();

    if (!Array.isArray(data)) {
      throw new Error('Invalid data format: Expected an array of people');
    }

    return {
      data,
      error: null,
      isLoading: false
    };
  } catch (error) {
    return {
      data: null,
      error: `Failed to load people data: ${error.message}`,
      isLoading: false
    };
  }
}

/**
 * Loads items data from items.json
 * @returns {Promise<{data: Object|null, error: string|null, isLoading: boolean}>}
 */
export async function loadItemsData() {
  try {
    const response = await fetch('/items.json');

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();

    if (typeof data !== 'object' || data === null || Array.isArray(data)) {
      throw new Error('Invalid data format: Expected an object with item mappings');
    }

    return {
      data,
      error: null,
      isLoading: false
    };
  } catch (error) {
    return {
      data: null,
      error: `Failed to load items data: ${error.message}`,
      isLoading: false
    };
  }
}

/**
 * Loads item slugs mapping from item-slugs.json
 * @returns {Promise<{data: Object|null, error: string|null, isLoading: boolean}>}
 */
export async function loadItemSlugs() {
  try {
    const response = await fetch('/item-slugs.json');

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();

    if (typeof data !== 'object' || data === null || Array.isArray(data)) {
      throw new Error('Invalid data format: Expected an object with slug mappings');
    }

    return {
      data,
      error: null,
      isLoading: false
    };
  } catch (error) {
    return {
      data: null,
      error: `Failed to load item slugs: ${error.message}`,
      isLoading: false
    };
  }
}

/**
 * Loads person slugs mapping from person-slugs.json
 * @returns {Promise<{data: Object|null, error: string|null, isLoading: boolean}>}
 */
export async function loadPersonSlugs() {
  try {
    const response = await fetch('/person-slugs.json');

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();

    if (typeof data !== 'object' || data === null || Array.isArray(data)) {
      throw new Error('Invalid data format: Expected an object with slug mappings');
    }

    return {
      data,
      error: null,
      isLoading: false
    };
  } catch (error) {
    return {
      data: null,
      error: `Failed to load person slugs: ${error.message}`,
      isLoading: false
    };
  }
}

/**
 * Loads and validates people data
 * @returns {Promise<{data: Array|null, error: string|null, isLoading: boolean, validationErrors: Array}>}
 */
export async function loadAndValidatePeopleData() {
  const result = await loadPeopleData();

  if (result.error || !result.data) {
    return {
      ...result,
      validationErrors: []
    };
  }

  const validation = validatePersonData(result.data);

  if (!validation.isValid) {
    return {
      data: null,
      error: `Data validation failed: ${validation.errors.map(e => e.message).join(', ')}`,
      isLoading: false,
      validationErrors: validation.errors
    };
  }

  return {
    ...result,
    validationErrors: []
  };
}

/**
 * Loads and validates items data
 * @returns {Promise<{data: Object|null, error: string|null, isLoading: boolean, validationErrors: Array}>}
 */
export async function loadAndValidateItemsData() {
  const result = await loadItemsData();

  if (result.error || !result.data) {
    return {
      ...result,
      validationErrors: []
    };
  }

  const validation = validateItemsData(result.data);

  if (!validation.isValid) {
    return {
      data: null,
      error: `Data validation failed: ${validation.errors.map(e => e.message).join(', ')}`,
      isLoading: false,
      validationErrors: validation.errors
    };
  }

  return {
    ...result,
    validationErrors: []
  };
}

/**
 * Loads both people and items data concurrently
 * @returns {Promise<{people: Array, items: Object, errors: Array, isLoading: boolean}>}
 */
export async function loadAllTeaCeremonyData() {
  try {
    const [peopleResult, itemsResult] = await Promise.all([
      loadAndValidatePeopleData(),
      loadAndValidateItemsData()
    ]);

    const errors = [];
    if (peopleResult.error) errors.push(peopleResult.error);
    if (itemsResult.error) errors.push(itemsResult.error);

    return {
      people: peopleResult.data || [],
      items: itemsResult.data || {},
      errors,
      isLoading: false
    };
  } catch (error) {
    return {
      people: [],
      items: {},
      errors: [`Failed to load tea ceremony data: ${error.message}`],
      isLoading: false
    };
  }
}

/**
 * Reloads data with retry logic
 * @param {Function} loadFunction - The load function to retry
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} delay - Delay between retries in milliseconds
 * @returns {Promise}
 */
export async function retryDataLoad(loadFunction, maxRetries = 3, delay = 1000) {
  let lastError = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await loadFunction();

      if (!result.error) {
        return result;
      }

      lastError = result.error;

      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * (attempt + 1)));
      }
    } catch (error) {
      lastError = error.message;

      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * (attempt + 1)));
      }
    }
  }

  return {
    data: null,
    error: `Failed after ${maxRetries} attempts. Last error: ${lastError}`,
    isLoading: false
  };
}

/**
 * Caches data in memory to avoid redundant network requests
 */
class DataCache {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.ttl = 5 * 60 * 1000; // 5 minutes TTL
  }

  get(key) {
    const timestamp = this.timestamps.get(key);

    if (!timestamp || Date.now() - timestamp > this.ttl) {
      this.cache.delete(key);
      this.timestamps.delete(key);
      return null;
    }

    return this.cache.get(key);
  }

  set(key, value) {
    this.cache.set(key, value);
    this.timestamps.set(key, Date.now());
  }

  clear() {
    this.cache.clear();
    this.timestamps.clear();
  }
}

const dataCache = new DataCache();

/**
 * Cached version of loadPeopleData
 * @param {boolean} forceRefresh - Force refresh from server
 * @returns {Promise}
 */
export async function loadPeopleDataCached(forceRefresh = false) {
  const cacheKey = 'people-data';

  if (!forceRefresh) {
    const cached = dataCache.get(cacheKey);
    if (cached) {
      return cached;
    }
  }

  const result = await loadAndValidatePeopleData();

  if (!result.error) {
    dataCache.set(cacheKey, result);
  }

  return result;
}

/**
 * Cached version of loadItemsData
 * @param {boolean} forceRefresh - Force refresh from server
 * @returns {Promise}
 */
export async function loadItemsDataCached(forceRefresh = false) {
  const cacheKey = 'items-data';

  if (!forceRefresh) {
    const cached = dataCache.get(cacheKey);
    if (cached) {
      return cached;
    }
  }

  const result = await loadAndValidateItemsData();

  if (!result.error) {
    dataCache.set(cacheKey, result);
  }

  return result;
}

/**
 * Cached version of loadItemSlugs
 * @param {boolean} forceRefresh - Force refresh from server
 * @returns {Promise}
 */
export async function loadItemSlugsCached(forceRefresh = false) {
  const cacheKey = 'item-slugs';

  if (!forceRefresh) {
    const cached = dataCache.get(cacheKey);
    if (cached) {
      return cached;
    }
  }

  const result = await loadItemSlugs();

  if (!result.error) {
    dataCache.set(cacheKey, result);
  }

  return result;
}

/**
 * Cached version of loadPersonSlugs
 */
export async function loadPersonSlugsCached(forceRefresh = false) {
  const cacheKey = 'person-slugs';

  if (!forceRefresh) {
    const cached = dataCache.get(cacheKey);
    if (cached) {
      return cached;
    }
  }

  const result = await loadPersonSlugs();
  if (!result.error) {
    dataCache.set(cacheKey, result);
  }
  return result;
}

/**
 * Clears all cached data
 */
export function clearDataCache() {
  dataCache.clear();
}

/**
 * Gets cache statistics
 * @returns {Object} Cache statistics
 */
export function getCacheStats() {
  return {
    size: dataCache.cache.size,
    keys: Array.from(dataCache.cache.keys()),
    timestamps: Array.from(dataCache.timestamps.entries()).map(([key, timestamp]) => ({
      key,
      timestamp,
      age: Date.now() - timestamp
    }))
  };
}